import "./FunnelDynamics.scss";
import { KpiData, KpiPreset } from "@blisspointmedia/bpm-types/dist/CrossChannel";
import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import * as R from "ramda";
import FunnelGraph from "funnel-graph-js";
import ChartContainer from "../../Components/ChartContainer";
import { Dropdown, DropdownToggleType, DownloadDropdown, InfoTooltip } from "../../Components";
import { exportToExcel, downloadPNG } from "../../utils/download-utils";
import { CrossChannelContext } from "../CrossChannel";
import { Brand30, Brand50 } from "../../utils/colors";
import { INACTIVE_COLOR } from "../../TVADCrossChannel/homePageConstants";
import { TrafficSource } from "./KpiVolume";

interface FunnelDynamicsData {
  labels: string[];
  subLabels: string[];
  colors: string[];
  values: number[][];
}

interface FunnelDynamicsProps {
  data: KpiData[];
  funnelPresets: KpiPreset[];
  defaultPreset?: string;
}

const SUB_LABELS = ["Paid", "Organic"];
const COLORS = [Brand50, Brand30];

const processKpiData = (
  data: KpiData[],
  kpiPreset: KpiPreset,
  focusedLabel: string
): FunnelDynamicsData => {
  const totalsByKpiOrganic = {};
  const totalsByKpiPaid = {};

  const presetKpis: string[] = R.pathOr([], ["kpis"], kpiPreset);

  for (let item of data) {
    const { kpi, count, isOrganic } = item;
    if (!presetKpis.includes(kpi)) {
      continue;
    }

    if ((focusedLabel === "Organic" && !isOrganic) || (focusedLabel === "Paid" && isOrganic)) {
      continue;
    }

    if (isOrganic) {
      totalsByKpiOrganic[kpi] = (totalsByKpiOrganic[kpi] || 0) + count;
    } else {
      totalsByKpiPaid[kpi] = (totalsByKpiPaid[kpi] || 0) + count;
    }
  }

  const values = presetKpis.map(kpi => [
    Math.round(totalsByKpiPaid[kpi] || 0),
    Math.round(totalsByKpiOrganic[kpi] || 0),
  ]);

  return {
    labels: presetKpis,
    subLabels: SUB_LABELS,
    colors: COLORS,
    values,
  };
};

const FunnelDynamics: React.FC<FunnelDynamicsProps> = React.memo(
  ({ data, funnelPresets, defaultPreset = "" }) => {
    const [preset, setPreset] = useState(defaultPreset);
    const [drawn, setDrawn] = useState(false);
    const [graph, setGraph] = useState<any>();
    const [focusedLabel, setFocusedLabel] = useState("");

    const { kpi, dates } = useContext(CrossChannelContext);

    const presetMap = useMemo(() => R.indexBy(R.prop("name"), funnelPresets), [funnelPresets]);

    useEffect(() => {
      if (defaultPreset === "") {
        const defaultPresetFromTable = R.find(row => row.is_default, funnelPresets);
        setPreset(defaultPresetFromTable?.name || funnelPresets[0]?.name || "");
      }
    }, [funnelPresets, defaultPreset]);

    const presetOptions = useMemo(
      () => funnelPresets.map(preset => ({ label: preset.name, value: preset.name })),
      [funnelPresets]
    );

    const processedData = useMemo(() => {
      if (!R.isNil(data) && !R.isEmpty(data) && preset) {
        return processKpiData(data, presetMap[preset], focusedLabel);
      }
    }, [data, preset, presetMap, focusedLabel]);

    useEffect(() => {
      if (drawn || !processedData) {
        return;
      }
      const graph = new FunnelGraph({
        container: ".funnelSVG",
        data: processedData,
        displayPercent: true,
        direction: "vertical",
        width: 350,
        height: 700,
      });

      graph.draw();
      setDrawn(true);
      setGraph(graph);
    }, [data, processedData, drawn]);

    const updateGraph = useCallback(
      (presetName: string) => {
        setPreset(presetName);

        if (graph && presetMap[presetName]) {
          const newData = processKpiData(data, presetMap[presetName], focusedLabel);
          graph.updateData(newData);
        }
      },
      [data, presetMap, graph, focusedLabel]
    );

    useEffect(() => {
      if (drawn && processedData) {
        updateGraph(preset || "");
      }
    }, [processedData, drawn, updateGraph, preset]);

    const fileNameIdentifiers = useMemo(() => {
      return ["CrossChannel", "FunnelDynamics", preset || "", `${dates.start}–${dates.end}`];
    }, [preset, dates]);

    const excelDownload = useCallback(() => {
      const exportData = processExport(data);
      exportToExcel(exportData, `${fileNameIdentifiers.join("_")}`);
    }, [data, fileNameIdentifiers]);

    const pngDownload = useCallback(async () => {
      const label = `KPI: ${kpi}`;
      await downloadPNG(".ccKpiFunnel", fileNameIdentifiers, label, [".cl-download"]);
    }, [kpi, fileNameIdentifiers]);

    const handleLabelChange = useCallback(
      label => setFocusedLabel(current => (current === label ? "" : label)),
      []
    );

    return (
      <ChartContainer
        title={
          <>
            <div>KPI Funnel</div>{" "}
            <InfoTooltip rightLabel="More info" size="sm">
              The KPI volumes shown on this tab represent non-attributed data. These are raw counts
              reflecting overall business activity and are not directly linked to specific
              advertising efforts or platforms.
            </InfoTooltip>
          </>
        }
        enableHoverDesign
        rightActions={
          <>
            <Dropdown
              size="sm"
              type={DropdownToggleType.FILLED}
              design="secondary"
              value={preset || ""}
              options={presetOptions}
              onChange={updateGraph}
            />
            <DownloadDropdown
              size="sm"
              onClickOptions={[excelDownload, pngDownload]}
              disabled={!data}
            />
          </>
        }
        footerContent={["Filters do not apply"]}
      >
        {R.isNil(processedData) ? (
          <div
            style={{
              display: "flex",
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
              fontSize: "26px",
              minHeight: "700px",
            }}
          >
            No KPI data to show
          </div>
        ) : (
          <div className="kpiFunnelDynamics">
            <div className="funnelLegend">
              {SUB_LABELS.map((label, index) => {
                return (
                  <div
                    className="legendItem"
                    key={label}
                    onClick={() => {
                      handleLabelChange(label);
                    }}
                  >
                    <div
                      className={"square"}
                      style={{
                        backgroundColor:
                          focusedLabel && focusedLabel !== label ? INACTIVE_COLOR : COLORS[index],
                      }}
                    />
                    <div
                      className={`label ${
                        focusedLabel && focusedLabel !== label ? "unselected" : ""
                      }`}
                    >
                      {label}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="funnelSVG" />
          </div>
        )}
      </ChartContainer>
    );
  }
);

const processExport = (data: KpiData[]) => {
  const result = new Map();

  data.forEach(({ date, kpi, count, isOrganic }) => {
    if (!result.has(date)) {
      result.set(date, { date });
    }

    const entry = result.get(date);

    const paidKey = `${kpi} - ${TrafficSource.Paid}`;
    const organicKey = `${kpi} - ${TrafficSource.Organic}`;
    const allKey = `${kpi} - All`;

    entry[paidKey] = entry[paidKey] || 0;
    entry[organicKey] = entry[organicKey] || 0;
    entry[allKey] = entry[allKey] || 0;

    const roundedCount = Math.round(count);

    if (isOrganic) {
      entry[organicKey] += roundedCount;
    } else {
      entry[paidKey] += roundedCount;
    }

    entry[allKey] += roundedCount;
  });

  return Array.from(result.values()).sort((a, b) => a.date.localeCompare(b.date));
};

export default FunnelDynamics;
